/**
 * useOfflineExitGuard — warns the user before leaving while offline.
 *
 * Behaviour (requirement):
 *  - While the app is offline (`navigator.onLine === false`), a
 *    `beforeunload` listener is registered so the browser shows its native
 *    "Leave site?" confirmation on tab close, reload or navigation.
 *  - If the user chooses to stay, a system toast is shown reminding them
 *    not to abandon the app while offline. The behaviour is recurrent: the
 *    next attempt shows the browser dialog again and, if they stay, the
 *    toast again.
 *  - When connectivity is restored, the listener is removed. When lost
 *    again, it is re-attached. Cleanup on unmount removes every listener.
 *
 * beforeunload limitation:
 *   Modern browsers do NOT expose a "user cancelled the dialog" event, so
 *   detecting that the user stayed is inherently indirect. The most robust
 *   workaround is to schedule a short `setTimeout` from inside the
 *   `beforeunload` handler: if the page actually unloads the timer never
 *   fires; if the page is still alive after the microtask window (~500 ms)
 *   the user must have cancelled — at that point we show the toast.
 *   This approach works consistently across Chromium, Firefox and Safari
 *   and does not depend on the `visibilitychange` event, which is unreliable
 *   for tab-close detection.
 */
import { useEffect } from 'react';
import { useToast } from '@/shared/ui/toast/ToastContext';
import { useTranslation } from 'react-i18next';

const OFFLINE_TOAST_ID = 'offline-exit-guard';
/** Delay after `beforeunload` fires before deciding the user stayed. */
const STAY_DETECTION_MS = 500;

export function useOfflineExitGuard(): void {
  const { showToast } = useToast();
  const { t } = useTranslation('common');

  useEffect(() => {
    let stayTimer: ReturnType<typeof setTimeout> | null = null;
    let listenerAttached = false;

    const clearStayTimer = () => {
      if (stayTimer !== null) {
        clearTimeout(stayTimer);
        stayTimer = null;
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Trigger the browser's native "Leave site?" confirmation.
      event.preventDefault();
      // Legacy Chrome/Edge requires a truthy returnValue string.
      event.returnValue = '';

      clearStayTimer();
      stayTimer = setTimeout(() => {
        // If we reach this point the page did NOT unload → user chose to stay.
        stayTimer = null;
        if (!navigator.onLine) {
          showToast(t('common.offlineExitGuard.stayWarning'), {
            id: OFFLINE_TOAST_ID,
            variant: 'warning',
            duration: 5000,
          });
        }
      }, STAY_DETECTION_MS);
    };

    const attach = () => {
      if (listenerAttached) return;
      window.addEventListener('beforeunload', handleBeforeUnload);
      listenerAttached = true;
    };

    const detach = () => {
      if (!listenerAttached) return;
      window.removeEventListener('beforeunload', handleBeforeUnload);
      listenerAttached = false;
    };

    const handleOnline = () => {
      clearStayTimer();
      detach();
    };
    const handleOffline = () => {
      attach();
    };

    // Sync with current state on mount.
    if (!navigator.onLine) attach();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearStayTimer();
      detach();
    };
  }, [showToast, t]);
}
